import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useCustomerAuth } from './CustomerAuthContext';
import { notify } from '../components/BalmartToaster';

const WishlistContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WishlistProvider({ children }) {
  const { customer } = useCustomerAuth();
  const [ids, setIds] = useState(new Set());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const reloadIds = useCallback(async () => {
    if (!customer) { setIds(new Set()); return; }
    try {
      const res = await axios.get(`${API}/wishlist/ids`, { withCredentials: true });
      setIds(new Set(res.data || []));
    } catch { setIds(new Set()); }
  }, [customer]);

  const reloadFull = useCallback(async () => {
    if (!customer) { setItems([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setItems(res.data || []);
      setIds(new Set((res.data || []).map(p => p.product_id)));
    } catch { setItems([]); }
    setLoading(false);
  }, [customer]);

  useEffect(() => { reloadIds(); }, [reloadIds]);

  const toggle = async (productId) => {
    if (!customer) return { requireAuth: true };
    const isIn = ids.has(productId);
    // optimistic UI
    setIds(prev => {
      const next = new Set(prev);
      isIn ? next.delete(productId) : next.add(productId);
      return next;
    });
    try {
      if (isIn) {
        await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
        notify.info('Retiré de vos favoris');
      } else {
        await axios.post(`${API}/wishlist`, { product_id: productId }, { withCredentials: true });
        notify.success('Ajouté à vos favoris');
      }
      return { success: true, added: !isIn };
    } catch (err) {
      // revert on error
      setIds(prev => {
        const next = new Set(prev);
        isIn ? next.add(productId) : next.delete(productId);
        return next;
      });
      return { success: false };
    }
  };

  const has = (productId) => ids.has(productId);

  return (
    <WishlistContext.Provider value={{ ids, items, loading, toggle, has, reloadFull, count: ids.size }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
